import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { DollarSign, Download, ExternalLink } from "lucide-react";
import { fiscalYearFor } from "@costadorada/shared";
import type { SummaryBreakdownItem } from "@costadorada/shared";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/cn";
import { QueryError } from "@/components/QueryError";

interface MonthlySummary {
  id: string;
  periodYear: number;
  periodMonth: number;
  totalIncome: string;
  totalExpenses: string;
  netIncome: string;
  reserveFund?: string | null;
  breakdown?: SummaryBreakdownItem[] | null;
  notes?: string | null;
  report?: { id: string; fileUrl: string } | null;
}

function monthName(month: number, lang: string): string {
  return new Intl.DateTimeFormat(lang.startsWith("es") ? "es-PR" : "en-US", {
    month: "long",
  }).format(new Date(2000, month - 1, 1));
}

export function ResidentFinancesPage() {
  const { t, i18n } = useTranslation();
  // The server only returns published summaries to residents
  const { data: summaries = [], isLoading, isError } = useQuery({
    queryKey: ["financial-summaries"],
    queryFn: async () => (await api.get<MonthlySummary[]>("/financial-summaries")).data,
  });

  const latest = summaries[0];
  const older = summaries.slice(1);

  if (isError) return <QueryError />;
  if (isLoading) return <div className="text-slate-500">{t("common.loading")}</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <DollarSign size={24} /> {t("residentFinances.title")}
        </h1>
        {latest && (
          <p className="text-sm text-slate-600 mt-1 capitalize">
            {t("residentFinances.subtitle", {
              month: monthName(latest.periodMonth, i18n.language),
              year: latest.periodYear,
            })}{" "}
            · {t("finance.close.fiscalYear")} {fiscalYearFor(latest.periodYear, latest.periodMonth)}
          </p>
        )}
      </div>

      {!latest ? (
        <div className="card text-slate-500">{t("residentFinances.empty")}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card">
              <div className="text-sm text-slate-500">{t("residentFinances.income")}</div>
              <div className="text-2xl font-bold mt-1">{formatCurrency(latest.totalIncome)}</div>
            </div>
            <div className="card">
              <div className="text-sm text-slate-500">{t("residentFinances.expenses")}</div>
              <div className="text-2xl font-bold mt-1">{formatCurrency(latest.totalExpenses)}</div>
            </div>
            <div className="card">
              <div className="text-sm text-slate-500">{t("residentFinances.net")}</div>
              <div
                className={`text-2xl font-bold mt-1 ${
                  parseFloat(latest.netIncome) >= 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                {formatCurrency(latest.netIncome)}
              </div>
            </div>
            <div className="card">
              <div className="text-sm text-slate-500">{t("residentFinances.reserve")}</div>
              <div className="text-2xl font-bold mt-1">
                {latest.reserveFund ? formatCurrency(latest.reserveFund) : "—"}
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500">{t("residentFinances.explainer")}</p>

          {latest.report?.fileUrl && (
            <a
              href={latest.report.fileUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-primary inline-flex"
            >
              <Download size={16} /> {t("residentFinances.downloadReport")}
            </a>
          )}

          {latest.breakdown && latest.breakdown.length > 0 && (
            <div className="card p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  {latest.breakdown.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2">{item.label}</td>
                      <td
                        className={`px-4 py-2 text-right font-medium ${
                          item.kind === "INCOME" ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {item.kind === "EXPENSE" ? "−" : ""}
                        {formatCurrency(Math.abs(item.amount))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {latest.notes && <p className="text-sm text-slate-600 italic">{latest.notes}</p>}

          {older.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-2">{t("residentFinances.previousMonths")}</h2>
              <div className="card p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-slate-100">
                    {older.map((s) => (
                      <tr key={s.id} className="hover:bg-slate-50">
                        <td className="px-4 py-2 capitalize">
                          {monthName(s.periodMonth, i18n.language)} {s.periodYear}
                        </td>
                        <td
                          className={`px-4 py-2 text-right font-medium ${
                            parseFloat(s.netIncome) >= 0 ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          {formatCurrency(s.netIncome)}
                        </td>
                        <td className="px-4 py-2 text-right w-24">
                          {s.report?.fileUrl && (
                            <a
                              href={s.report.fileUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary hover:underline inline-flex items-center gap-1"
                            >
                              <ExternalLink size={12} /> PDF
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
